import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { useCreateTask } from "@/application/use-cases/tasks/use-tasks.queries";
import { useCurrentPhase } from "@/application/use-cases/phases/use-phases.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { PageHeader } from "@/presentation/components/shell/page-header";
import { WeekSelect } from "@/presentation/components/weeks/week-select";
import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import type { Priority, TaskStatus } from "@/infrastructure/supabase/database.types";

export const Route = createFileRoute("/_app/tasks/new")({
  component: NewTaskPage,
});

function NewTaskPage() {
  const navigate = useNavigate();
  const create = useCreateTask();
  const currentPhase = useCurrentPhase();
  const stakeholdersQ = useStakeholdersList();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<TaskStatus>("todo");
  const [priority, setPriority] = useState<Priority>("medium");
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [phaseId, setPhaseId] = useState<string | null>(null);
  const [phaseTouched, setPhaseTouched] = useState(false);

  const effectivePhaseId = phaseTouched ? phaseId : (currentPhase.data?.id ?? null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await create.mutateAsync({
        title,
        description: description || null,
        status,
        priority,
        assignee_id: assigneeId,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        phase_id: effectivePhaseId,
      });
      toast.success("Task created");
      void navigate({ to: "/tasks", search: { id: created.id } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create");
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader title="New task" />
      <form onSubmit={onSubmit} className="flex max-w-2xl flex-col gap-4 px-6 py-6">
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <Select value={status} onValueChange={(v) => setStatus(v as TaskStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todo">To do</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="done">Done</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={priority} onValueChange={(v) => setPriority(v as Priority)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Assignee">
            <Select
              value={assigneeId ?? "__none__"}
              onValueChange={(v) => setAssigneeId(v === "__none__" ? null : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {(stakeholdersQ.data ?? []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}{" "}
                    <span className="text-muted-foreground text-[10px] uppercase">· {s.org}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Due date">
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </Field>
        </div>
        <Field label="Week">
          <WeekSelect
            value={effectivePhaseId}
            onChange={(next) => {
              setPhaseTouched(true);
              setPhaseId(next);
            }}
            ariaLabel="Task week"
          />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => void navigate({ to: "/tasks" })}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-[10px] tracking-wider uppercase">{label}</Label>
      {children}
    </div>
  );
}
